import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef, useMemo } from 'react';
import { getUseOnlineApi } from '../sqliteDb';
import { useElectronEnvironment } from '../hooks/useElectronEnvironment';

const MommContext = createContext();
const MommDispatchContext = createContext();

const REQUEST_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
  RECOVERABLE: 'recoverable'
};

// Determine if we should start with loading state
const getInitialLoadingState = () => {
  // Check if we're in browser (always online) or electron with online mode enabled
  const isBrowser = typeof window !== 'undefined' && !window.require;
  const isOnlineMode = isBrowser || getUseOnlineApi();
  
  // Start with loading state in online mode to prevent flashing
  return isOnlineMode;
};

const initialState = {
  // Chat state
  messages: [
    { id: 1, text: "Hello! I'm MoMM, powered by Promptus. I can understand both text and images. How can I assist you today?", sender: 'bot', role: 'assistant' }
  ],
  apiMessages: [
    { role: "system", content: "You are a helpful assistant who can analyze both text and images. When asked to modify a previously generated image (like changing colors, style, etc.), remember to reference the previous image and be specific about what should be changed while maintaining the other aspects of the image. Provide complete descriptions for any image generation." }
  ],
  currentChatId: null,
  
  // Input state
  inputMessage: '',
  selectedImages: [],
  imageFiles: [],
  
  // Loading states
  isLoading: false,
  isGeneratingSong: false,
  isGeneratingVideo: false,
  isClearingHistory: false,
  isLoadingHistory: getInitialLoadingState(),
  loadingChatId: null, // Track which specific chat is being loaded
  
  // Progress tracking
  songProgress: 0,
  videoProgress: 0,
  songGenerationProgress: null,
  videoGenerationProgress: null,
  songStatusCheckInterval: null,
  videoStatusCheckInterval: null,
  
  // UI state
  enlargedImage: null,
  isImageModalOpen: false,
  clickedImageUrl: null,
  sidebarOpen: false,
  currentImageIndex: 0,
  inputPlaceholder: "Message MoMM...",
  isMessageDirectionReversed: false,
  isAppVisible: true,
  
  // Chat management
  chatHistory: [],
  isExplicitNewChat: false,
  isChatListOpen: false,
  
  // Collection tracking
  collectionImages: {},
  
  // Request tracking
  activeRequests: {},
  requestResults: {},
  
  // Thinking messages tracking per chat
  thinkingMessagesByChat: {},
  
  // Cache
  imageBase64Cache: {},
  cachedImages: {},
  
  // Last generated content
  lastGeneratedImage: null,
  lastGeneratedPrompt: '',
  
  // Staged import from CollectionImage/Playground
  stagedImport: null, // {url, name, prompt, fromComfyUI, fromPlayground, timestamp}
  
  // Image context tracking for GPT-4o
  recentImageGenerations: [], // Array of {imageUrl, prompt, timestamp, messageId}
  maxRecentImages: 5, // Maximum number of recent images to track
  autoIncludeImageContext: true, // Auto-include recent images in GPT-4o context
};

function mommReducer(state, action) {
  switch (action.type) {
    case 'SET_MESSAGES':
      return { ...state, messages: Array.isArray(action.payload) ? action.payload : [] };
      
    case 'UPDATE_MESSAGES':
      // Support callback pattern for updating messages
      if (typeof action.payload === 'function') {
        const newMessages = action.payload(state.messages);
        return { ...state, messages: Array.isArray(newMessages) ? newMessages : [] };
      }
      return { ...state, messages: Array.isArray(action.payload) ? action.payload : [] };
      
    case 'ADD_MESSAGE':
      return { ...state, messages: [...state.messages, action.payload] };
      
    case 'UPDATE_MESSAGE':
      return {
        ...state,
        messages: Array.isArray(state.messages) ? state.messages.map(msg =>
          msg.id === action.payload.id ? { ...msg, ...action.payload.updates } : msg
        ) : []
      };
      
    case 'SET_API_MESSAGES':
      return { ...state, apiMessages: action.payload };
      
    case 'ADD_API_MESSAGE':
      return { ...state, apiMessages: [...state.apiMessages, action.payload] };
      
    case 'SET_INPUT_MESSAGE':
      return { ...state, inputMessage: action.payload };
      
    case 'SET_SELECTED_IMAGES':
      return { ...state, selectedImages: action.payload };
      
    case 'SET_IMAGE_FILES':
      return { ...state, imageFiles: action.payload };
      
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
      
    case 'SET_GENERATING_SONG':
      return { ...state, isGeneratingSong: action.payload };
      
    case 'SET_GENERATING_VIDEO':
      return { ...state, isGeneratingVideo: action.payload };
      
    case 'SET_SONG_PROGRESS':
      return { ...state, songProgress: action.payload };
      
    case 'SET_VIDEO_PROGRESS':
      return { ...state, videoProgress: action.payload };
      
    case 'SET_CURRENT_CHAT_ID':
      return { ...state, currentChatId: action.payload };
      
    case 'SET_ENLARGED_IMAGE':
      return { ...state, enlargedImage: action.payload };
      
    case 'SET_IMAGE_MODAL_OPEN':
      return { ...state, isImageModalOpen: action.payload };
      
    case 'SET_CLICKED_IMAGE_URL':
      return { ...state, clickedImageUrl: action.payload };
      
    case 'SET_SIDEBAR_OPEN':
      return { ...state, sidebarOpen: action.payload };
      
    case 'SET_LAST_GENERATED_IMAGE':
      return { ...state, lastGeneratedImage: action.payload };
      
    case 'SET_LAST_GENERATED_PROMPT':
      return { ...state, lastGeneratedPrompt: action.payload };
      
    case 'SET_STAGED_IMPORT':
      return { ...state, stagedImport: action.payload };
      
    case 'CLEAR_STAGED_IMPORT':
      return { ...state, stagedImport: null };
      
    case 'ADD_ACTIVE_REQUEST':
      return {
        ...state,
        activeRequests: {
          ...state.activeRequests,
          [action.payload.id]: action.payload
        }
      };
      
    case 'UPDATE_REQUEST_STATUS':
      return {
        ...state,
        activeRequests: {
          ...state.activeRequests,
          [action.payload.id]: {
            ...state.activeRequests[action.payload.id],
            status: action.payload.status,
            updatedAt: Date.now()
          }
        }
      };
      
    case 'REMOVE_ACTIVE_REQUEST':
      const { [action.payload]: removed, ...remainingRequests } = state.activeRequests;
      return {
        ...state,
        activeRequests: remainingRequests
      };
      
    case 'SET_REQUEST_RESULT':
      return {
        ...state,
        requestResults: {
          ...state.requestResults,
          [action.payload.id]: action.payload.result
        }
      };
      
    case 'ADD_IMAGE_TO_CACHE':
      return {
        ...state,
        imageBase64Cache: {
          ...state.imageBase64Cache,
          [action.payload.key]: action.payload.value
        }
      };
      
    case 'CLEAR_IMAGE_CACHE':
      return { ...state, imageBase64Cache: {} };
      
    case 'RESET_STATE':
      return initialState;
      
    case 'RESTORE_STATE':
      const restoredState = { ...state, ...action.payload };
      // Ensure messages is always an array
      if (!Array.isArray(restoredState.messages)) {
        restoredState.messages = initialState.messages;
      }
      // Ensure apiMessages is always an array
      if (!Array.isArray(restoredState.apiMessages)) {
        restoredState.apiMessages = initialState.apiMessages;
      }
      return restoredState;
      
    case 'SET_CHAT_HISTORY':
      return { ...state, chatHistory: action.payload };
      
    case 'SET_IS_EXPLICIT_NEW_CHAT':
      return { ...state, isExplicitNewChat: action.payload };
      
    case 'SET_IS_CHAT_LIST_OPEN':
      return { ...state, isChatListOpen: action.payload };
      
    case 'SET_COLLECTION_IMAGES':
      return { ...state, collectionImages: action.payload };
      
    case 'UPDATE_COLLECTION_IMAGE':
      return {
        ...state,
        collectionImages: {
          ...state.collectionImages,
          [action.payload.hash]: action.payload.value
        }
      };
      
    case 'SET_CURRENT_IMAGE_INDEX':
      return { ...state, currentImageIndex: action.payload };
      
    case 'SET_SONG_GENERATION_PROGRESS':
      return { ...state, songGenerationProgress: action.payload };
      
    case 'SET_VIDEO_GENERATION_PROGRESS':
      return { ...state, videoGenerationProgress: action.payload };
      
    case 'SET_SONG_STATUS_CHECK_INTERVAL':
      return { ...state, songStatusCheckInterval: action.payload };
      
    case 'SET_VIDEO_STATUS_CHECK_INTERVAL':
      return { ...state, videoStatusCheckInterval: action.payload };
      
    case 'SET_INPUT_PLACEHOLDER':
      return { ...state, inputPlaceholder: action.payload };
      
    case 'SET_IS_MESSAGE_DIRECTION_REVERSED':
      return { ...state, isMessageDirectionReversed: action.payload };
      
    case 'SET_IS_APP_VISIBLE':
      return { ...state, isAppVisible: action.payload };
      
    case 'SET_CACHED_IMAGES':
      return { ...state, cachedImages: action.payload };
      
    case 'ADD_CACHED_IMAGE':
      return {
        ...state,
        cachedImages: {
          ...state.cachedImages,
          [action.payload.key]: action.payload.value
        }
      };
      
    case 'SET_IS_CLEARING_HISTORY':
      return { ...state, isClearingHistory: action.payload };
      
    case 'SET_IS_LOADING_HISTORY':
      return { ...state, isLoadingHistory: action.payload };
      
    case 'SET_LOADING_CHAT_ID':
      return { ...state, loadingChatId: action.payload };
      
    case 'ADD_THINKING_MESSAGE':
      return {
        ...state,
        thinkingMessagesByChat: {
          ...state.thinkingMessagesByChat,
          [action.payload.chatId]: [
            ...(state.thinkingMessagesByChat[action.payload.chatId] || []),
            {
              messageId: action.payload.messageId,
              requestId: action.payload.requestId,
              timestamp: action.payload.timestamp,
              text: action.payload.text || "Thinking..."
            }
          ]
        }
      };
      
    case 'REMOVE_THINKING_MESSAGE':
      return {
        ...state,
        thinkingMessagesByChat: {
          ...state.thinkingMessagesByChat,
          [action.payload.chatId]: (state.thinkingMessagesByChat[action.payload.chatId] || [])
            .filter(msg => msg.messageId !== action.payload.messageId)
        }
      };
      
    case 'CLEAR_CHAT_THINKING_MESSAGES':
      return {
        ...state,
        thinkingMessagesByChat: {
          ...state.thinkingMessagesByChat,
          [action.payload.chatId]: []
        }
      };
      
    case 'ADD_RECENT_IMAGE_GENERATION':
      const newGeneration = action.payload;
      const updatedGenerations = [newGeneration, ...state.recentImageGenerations]
        .slice(0, state.maxRecentImages); // Keep only the most recent images
      return {
        ...state,
        recentImageGenerations: updatedGenerations
      };
      
    case 'CLEAR_RECENT_IMAGE_GENERATIONS':
      return {
        ...state,
        recentImageGenerations: []
      };
      
    case 'SET_AUTO_INCLUDE_IMAGE_CONTEXT':
      return {
        ...state,
        autoIncludeImageContext: action.payload
      };
      
    case 'SET_MAX_RECENT_IMAGES':
      return {
        ...state,
        maxRecentImages: action.payload,
        // Trim existing array if necessary
        recentImageGenerations: state.recentImageGenerations.slice(0, action.payload)
      };
      
    default:
      return state;
  }
}

export function MommProvider({ children }) {
  const [state, dispatch] = useReducer(mommReducer, initialState);
  const requestHandlers = useRef({});
  const progressIntervals = useRef({});
  
  // Additional refs for MOMM functionality
  const refs = useRef({
    messageIdCounter: 0,
    lastTimestamp: 0,
    processedSongIds: new Set(),
    isSongCheckInProgress: false,
    completedWorkIds: new Set(),
    currentSongLoadingMessageId: null,
    songMessageLock: false,
    addedAudioUrls: new Set(),
    messagesEnd: null,
    fileInput: null,
    recoveryRanForChat: null,
  });
  
  // Load persisted state from sessionStorage on mount
  useEffect(() => {
    const savedState = sessionStorage.getItem('mommState');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        const { activeRequests, ...stateToRestore } = parsedState;
        
        // Process active requests - mark recent ones as recoverable, old ones as failed
        const processedRequests = {};
        const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
        
        if (activeRequests) {
          Object.entries(activeRequests).forEach(([id, request]) => {
            if (request.createdAt > fiveMinutesAgo && 
                (request.status === 'pending' || request.status === 'in_progress')) {
              // Mark as recoverable for recent requests
              processedRequests[id] = {
                ...request,
                status: 'recoverable',
                recoveredAt: Date.now()
              };
              console.log(`🔄 Found recoverable request: ${id}`);
            } else if (request.status === 'pending' || request.status === 'in_progress') {
              // Mark old incomplete requests as failed
              processedRequests[id] = {
                ...request,
                status: 'failed',
                error: 'Request timeout - session was closed'
              };
              console.log(`❌ Marking stale request as failed: ${id}`);
            }
            // Completed/failed/cancelled requests are preserved as-is
            else {
              processedRequests[id] = request;
            }
          });
        }
        
        // Don't restore messages or chatHistory from sessionStorage
        // They will be loaded from SQLite when needed
        const { messages, chatHistory, apiMessages, selectedImages, ...restoreableState } = stateToRestore;
        
        // Restore selectedImages - we only have lightweight references
        // The actual image data will need to be re-loaded if needed
        const restoredSelectedImages = selectedImages?.map(imgRef => ({
          name: imgRef.name,
          // If it was a data URL, we can't restore it (it's too large)
          // The UI will need to handle this gracefully
          url: imgRef.url || (imgRef.isDataUrl ? '' : null)
        })).filter(img => img.name) || [];
        
        dispatch({ type: 'RESTORE_STATE', payload: {
          ...restoreableState,
          selectedImages: restoredSelectedImages,
          activeRequests: processedRequests,
          // Keep initial state for data that should be loaded from DB
          messages: initialState.messages,
          apiMessages: initialState.apiMessages,
          chatHistory: []
        }});
        
        // Note: The MOMM component will load messages from SQLite
        // when it mounts if currentChatId is set
      } catch (error) {
        console.error('Failed to restore MOMM state:', error);
      }
    }
  }, []);
  
  // Save state to sessionStorage on changes
  useEffect(() => {
    try {
      // Only save UI state and temporary data, NOT data that's already in SQLite
      const stateToSave = {
        // Current session info
        currentChatId: state.currentChatId,
        
        // UI state
        inputMessage: state.inputMessage,
        // Store only lightweight image references, not full data URLs
        selectedImages: state.selectedImages.map(img => ({
          name: img.name,
          // Store type indicator instead of full data URL
          isDataUrl: img.url?.startsWith('data:'),
          // Store size estimate for data URLs
          sizeEstimate: img.url?.startsWith('data:') ? img.url.length : 0,
          // For non-data URLs, keep the URL
          url: img.url?.startsWith('data:') ? null : img.url
        })),
        sidebarOpen: state.sidebarOpen,
        isChatListOpen: state.isChatListOpen,
        currentImageIndex: state.currentImageIndex,
        inputPlaceholder: state.inputPlaceholder,
        isMessageDirectionReversed: state.isMessageDirectionReversed,
        isImageModalOpen: state.isImageModalOpen,
        // Don't store full image URL if it's a data URL
        clickedImageUrl: state.clickedImageUrl?.startsWith('data:') ? null : state.clickedImageUrl,
        
        // Temporary/loading states
        isLoading: state.isLoading,
        isGeneratingSong: state.isGeneratingSong,
        isGeneratingVideo: state.isGeneratingVideo,
        songProgress: state.songProgress,
        videoProgress: state.videoProgress,
        
        // Last generated content (for quick access)
        // Don't store large image data URLs
        lastGeneratedImage: state.lastGeneratedImage?.startsWith('data:') ? null : state.lastGeneratedImage,
        lastGeneratedPrompt: state.lastGeneratedPrompt,
        
        // Only save minimal activeRequests metadata (not full data)
        activeRequests: Object.entries(state.activeRequests).reduce((acc, [id, req]) => {
          acc[id] = {
            id: req.id,
            type: req.type,
            status: req.status,
            createdAt: req.createdAt,
            updatedAt: req.updatedAt,
            // Don't save the full data object which may contain images
          };
          return acc;
        }, {}),
        
        // Thinking messages are temporary UI state
        thinkingMessagesByChat: state.thinkingMessagesByChat
      };
      
      sessionStorage.setItem('mommState', JSON.stringify(stateToSave));
    } catch (error) {
      // Handle quota exceeded or other storage errors
      if (error.name === 'QuotaExceededError') {
        console.warn('SessionStorage quota exceeded, clearing and retrying with minimal state');
        try {
          // Clear sessionStorage and save only essential state
          sessionStorage.removeItem('mommState');
          const minimalState = {
            currentChatId: state.currentChatId,
            inputMessage: state.inputMessage,
            isLoading: state.isLoading,
            isChatListOpen: state.isChatListOpen
          };
          sessionStorage.setItem('mommState', JSON.stringify(minimalState));
        } catch (retryError) {
          console.error('Failed to save even minimal state:', retryError);
        }
      } else {
        console.error('Error saving state to sessionStorage:', error);
      }
    }
  }, [
    state.currentChatId,
    state.inputMessage,
    state.selectedImages,
    state.sidebarOpen,
    state.isChatListOpen,
    state.currentImageIndex,
    state.inputPlaceholder,
    state.isMessageDirectionReversed,
    state.isImageModalOpen,
    state.clickedImageUrl,
    state.isLoading,
    state.isGeneratingSong,
    state.isGeneratingVideo,
    state.songProgress,
    state.videoProgress,
    state.lastGeneratedImage,
    state.lastGeneratedPrompt,
    state.activeRequests,
    state.thinkingMessagesByChat
  ]);
  
  // Request management functions
  const createRequest = useCallback((type, data, handler) => {
    const requestId = `${type}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const request = {
      id: requestId,
      type,
      data,
      status: REQUEST_STATUS.PENDING,
      createdAt: Date.now(),
      updatedAt: Date.now()
    };
    
    dispatch({ type: 'ADD_ACTIVE_REQUEST', payload: request });
    console.log(`📝 Created new ${type} request: ${requestId}`);
    
    if (handler) {
      requestHandlers.current[requestId] = handler;
    }
    
    return requestId;
  }, []);
  
  const updateRequestStatus = useCallback((requestId, status) => {
    dispatch({ type: 'UPDATE_REQUEST_STATUS', payload: { id: requestId, status } });
    console.log(`📊 Updated request ${requestId} status to: ${status}`);
    
    if (status === REQUEST_STATUS.COMPLETED || status === REQUEST_STATUS.FAILED || status === REQUEST_STATUS.CANCELLED) {
      // Clean up handlers and intervals
      delete requestHandlers.current[requestId];
      if (progressIntervals.current[requestId]) {
        clearInterval(progressIntervals.current[requestId]);
        delete progressIntervals.current[requestId];
      }
      
      // Remove the request from activeRequests after a small delay to ensure UI updates
      setTimeout(() => {
        dispatch({ type: 'REMOVE_ACTIVE_REQUEST', payload: requestId });
        console.log(`🧹 Removed ${status} request ${requestId} from activeRequests`);
      }, 100);
    }
  }, []);
  
  const cancelRequest = useCallback((requestId) => {
    const handler = requestHandlers.current[requestId];
    if (handler && handler.cancel) {
      handler.cancel();
    }
    updateRequestStatus(requestId, REQUEST_STATUS.CANCELLED);
  }, [updateRequestStatus]);
  
  const setRequestResult = useCallback((requestId, result) => {
    dispatch({ type: 'SET_REQUEST_RESULT', payload: { id: requestId, result } });
  }, []);
  
  // Register a handler for an existing request
  const registerRequestHandler = useCallback((requestId, handler) => {
    if (requestId && handler) {
      requestHandlers.current[requestId] = handler;
    }
  }, []);
  
  // Progress tracking for long-running operations
  const startProgressTracking = useCallback((requestId, type, updateInterval = 1000) => {
    if (progressIntervals.current[requestId]) {
      clearInterval(progressIntervals.current[requestId]);
    }
    
    let progress = 0;
    progressIntervals.current[requestId] = setInterval(() => {
      progress = Math.min(progress + (100 / 30), 95); // Max 95% until completion
      
      if (type === 'song') {
        dispatch({ type: 'SET_SONG_PROGRESS', payload: progress });
      } else if (type === 'video') {
        dispatch({ type: 'SET_VIDEO_PROGRESS', payload: progress });
      }
    }, updateInterval);
  }, []);
  
  const stopProgressTracking = useCallback((requestId, type) => {
    if (progressIntervals.current[requestId]) {
      clearInterval(progressIntervals.current[requestId]);
      delete progressIntervals.current[requestId];
    }
    
    if (type === 'song') {
      dispatch({ type: 'SET_SONG_PROGRESS', payload: 100 });
      setTimeout(() => dispatch({ type: 'SET_SONG_PROGRESS', payload: 0 }), 1000);
    } else if (type === 'video') {
      dispatch({ type: 'SET_VIDEO_PROGRESS', payload: 100 });
      setTimeout(() => dispatch({ type: 'SET_VIDEO_PROGRESS', payload: 0 }), 1000);
    }
  }, []);
  
  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Cancel all active requests
      Object.keys(requestHandlers.current).forEach(requestId => {
        cancelRequest(requestId);
      });
      
      // Clear all intervals
      Object.values(progressIntervals.current).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, [cancelRequest]);
  
  // Utility functions
  const generateUniqueId = useCallback(() => {
    let timestamp = Date.now();
    if (timestamp <= refs.current.lastTimestamp) {
      timestamp = refs.current.lastTimestamp + 1;
    }
    refs.current.lastTimestamp = timestamp;
    
    const counter = ++refs.current.messageIdCounter;
    return `${timestamp}-${counter}`;
  }, []);
  
  const clearSelectedImages = useCallback(() => {
    dispatch({ type: 'SET_SELECTED_IMAGES', payload: [] });
    dispatch({ type: 'SET_IMAGE_FILES', payload: [] });
  }, []);
  
  const addMessage = useCallback((message) => {
    dispatch({ type: 'ADD_MESSAGE', payload: message });
  }, []);
  
  const updateMessage = useCallback((id, updates) => {
    dispatch({ type: 'UPDATE_MESSAGE', payload: { id, updates } });
  }, []);
  
  const contextValue = useMemo(() => ({
    ...state,
    createRequest,
    updateRequestStatus,
    cancelRequest,
    setRequestResult,
    registerRequestHandler,
    startProgressTracking,
    stopProgressTracking,
    generateUniqueId,
    clearSelectedImages,
    addMessage,
    updateMessage,
    refs: refs.current,
  }), [state, createRequest, updateRequestStatus, cancelRequest, setRequestResult, registerRequestHandler, startProgressTracking, stopProgressTracking, generateUniqueId, clearSelectedImages, addMessage, updateMessage]);
  
  return (
    <MommContext.Provider value={contextValue}>
      <MommDispatchContext.Provider value={dispatch}>
        {children}
      </MommDispatchContext.Provider>
    </MommContext.Provider>
  );
}

export function useMommContext() {
  const context = useContext(MommContext);
  if (!context) {
    throw new Error('useMommContext must be used within a MommProvider');
  }
  return context;
}

export function useMommDispatch() {
  const dispatch = useContext(MommDispatchContext);
  if (!dispatch) {
    throw new Error('useMommDispatch must be used within a MommProvider');
  }
  return dispatch;
}